import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Typography,
  Tabs,
  Tab,
  FormControlLabel,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import { useState } from 'react';
import GenericModal from 'src/components/generic-modal';
import { 
  EmailOutlined, 
  PhonelinkRingOutlined, 
  AttachFileOutlined 
} from '@mui/icons-material';

export default function SendToDriverModal({
  open,
  handleClose,
  onSend,
  isLoading = false,
  driver,
}) {
  const [tabValue, setTabValue] = useState(1);
  const [emailData, setEmailData] = useState({ cc: '', subject: '', message: '' });
  const [selectedDocuments, setSelectedDocuments] = useState({
    fileName: true,
    manifestPdf: true,
  });

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleEmailDataChange = (field) => (event) => {
    setEmailData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleDocumentToggle = (doc) => {
    setSelectedDocuments((prev) => ({ ...prev, [doc]: !prev[doc] }));
  };

  const handleResendToDriver = () => {
    const payload = tabValue === 0 
      ? { method: 'mobile_only' } 
      : { method: 'email_and_mobile', emailData, documents: selectedDocuments };
    onSend(payload);
  };

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title="Resend to Driver"
      maxWidth="sm"
      desktopWidth="550px"
      showCloseIcon={true}
      hideFooter={true}
    >
      <Box sx={{ mt: -1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          // Removed fullWidth to make tabs smaller
          sx={{ 
            mb: 3, 
            borderBottom: 1, 
            borderColor: 'divider',
            minHeight: '40px', // Smaller container
          }}
        >
          <Tab 
            icon={<PhonelinkRingOutlined sx={{ fontSize: '18px !important' }} />} 
            iconPosition="start" 
            label="App Only" 
            sx={{ 
                textTransform: 'none', 
                fontWeight: 600, 
                minHeight: '48px', // Smaller height
                fontSize: '0.85rem' 
            }}
          />
          <Tab 
            icon={<EmailOutlined sx={{ fontSize: '18px !important' }} />} 
            iconPosition="start" 
            label="Email & App" 
            sx={{ 
                textTransform: 'none', 
                fontWeight: 600, 
                minHeight: '48px', // Smaller height
                fontSize: '0.85rem' 
            }}
          />
        </Tabs>

        {/* --- Content Area --- */}
        <Box sx={{ minHeight: '280px' }}>
          {tabValue === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderStyle: 'dashed' }}>
              <Typography variant="body2" color="text.secondary">
                This will send a direct push notification to the driver's mobile device.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2.5}>
              {/* Recipient Info */}
               <Paper variant="outlined" sx={{ py: 1, px: 2, bgcolor: 'grey.50' }}>
              <Box>
                <Typography variant="caption" fontWeight="700" color="text.secondary" textTransform="uppercase">
                  Driver Recipient
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {driver?.firstName} {driver?.lastName || 'Fares Saab'}
                </Typography>
              </Box>
              </Paper>

              {/* Email Form */}
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  variant="outlined" // Explicitly outlined
                  label="CC Recipients"
                  placeholder="email@example.com"
                  size="small"
                  value={emailData.cc}
                  onChange={handleEmailDataChange('cc')}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Subject"
                  size="small"
                  value={emailData.subject}
                  onChange={handleEmailDataChange('subject')}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  label="Message"
                  placeholder="Add an optional note to the driver..."
                  value={emailData.message}
                  onChange={handleEmailDataChange('message')}
                />
              </Stack>

              {/* Attachments Section */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <AttachFileOutlined fontSize="small" color="action" />
                  <Typography variant="caption" fontWeight="700" color="text.secondary" textTransform="uppercase">
                    Attachments
                  </Typography>
                </Stack>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                  <Stack direction="row" spacing={2}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={selectedDocuments.fileName} onChange={() => handleDocumentToggle('fileName')} />}
                      label={<Typography variant="body2">File Name</Typography>}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={selectedDocuments.manifestPdf} onChange={() => handleDocumentToggle('manifestPdf')} />}
                      label={<Typography variant="body2">Manifest PDF</Typography>}
                    />
                  </Stack>
                </Paper>
              </Box>
            </Stack>
          )}
        </Box>

        {/* --- Footer Actions --- */}
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => onSend({ method: 'mark_only' })}
            disabled={isLoading}
            sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            Mark as sent only
          </Button>
          
          <Stack direction="row" spacing={1.5}>
            <Button 
              variant="outlined" 
              onClick={handleClose} 
              disabled={isLoading} 
              sx={{ textTransform: 'none', borderRadius: '8px', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleResendToDriver}
              disabled={isLoading}
              elevation={0}
              sx={{ 
                textTransform: 'none', 
                borderRadius: '8px', 
                px: 3,
                backgroundColor: '#1e55bf',
                '&:hover': { backgroundColor: '#16449e' }
              }}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Resend to Driver'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </GenericModal>
  );
}